// Game Settings System
// Manages global game settings with localStorage persistence

const SETTINGS_STORAGE_KEY = 'snordeluxe_game_settings';

export const BUTTON_THEMES = ['slate', 'royal-blue', 'classic-orange', 'mint', 'crimson'];
const DEFAULT_BUTTON_THEME = 'slate';

export const BUTTON_STYLES = ['solid', 'glass', 'pill', 'outline', 'neon'];
const DEFAULT_BUTTON_STYLE = 'solid';

// Default settings
const DEFAULT_SETTINGS = {
    disableMultiplayer: false,
    buttonTheme: DEFAULT_BUTTON_THEME,
    buttonStyle: DEFAULT_BUTTON_STYLE,
};

let gameSettings = { ...DEFAULT_SETTINGS };

/**
 * Load settings from localStorage
 */
export function loadSettings() {
    try {
        const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
        if (stored) {
            gameSettings = { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
        }
    } catch (error) {
        console.warn('Failed to load settings from localStorage:', error);
        gameSettings = { ...DEFAULT_SETTINGS };
    }

    // One-time migration: the old default was disableMultiplayer=true,
    // which silently routed every "Host New Match" click to single-player.
    // Anyone whose settings were saved under that default has it stuck on,
    // and there's no UI to toggle it. Force it off and re-persist so old
    // browsers stop sandbagging multiplayer.
    if (gameSettings.disableMultiplayer === true) {
        gameSettings.disableMultiplayer = false;
        try {
            localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(gameSettings));
        } catch (_) { /* best-effort */ }
    }

    console.log('Game settings loaded:', gameSettings);
    return gameSettings;
}

/**
 * Save settings to localStorage
 */
export function saveSettings() {
    try {
        localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(gameSettings));
        console.log('Game settings saved:', gameSettings);
    } catch (error) {
        console.error('Failed to save settings to localStorage:', error);
    }
}

/**
 * Get a specific setting value
 * @param {string} key - The setting key
 * @returns {any} The setting value
 */
export function getSetting(key) {
    return gameSettings[key];
}

/**
 * Set a specific setting value
 * @param {string} key - The setting key
 * @param {any} value - The setting value
 */
export function setSetting(key, value) {
    gameSettings[key] = value;
    saveSettings();
}

/**
 * Get all current settings
 * @returns {object} All current settings
 */
export function getAllSettings() {
    return { ...gameSettings };
}

/**
 * Reset settings to defaults
 */
export function resetSettings() {
    gameSettings = { ...DEFAULT_SETTINGS };
    saveSettings();
}

/**
 * Check if multiplayer is disabled
 * @returns {boolean} True if multiplayer should be disabled
 */
export function isMultiplayerDisabled() {
    return getSetting('disableMultiplayer') === true;
}

/**
 * Apply a button theme by setting data-button-theme on <body>.
 * Falls back to default if the theme name isn't known.
 */
export function applyButtonTheme(theme) {
    const safeTheme = BUTTON_THEMES.includes(theme) ? theme : DEFAULT_BUTTON_THEME;
    if (document.body) {
        document.body.dataset.buttonTheme = safeTheme;
    }
    setSetting('buttonTheme', safeTheme);
    refreshThemeSwatchActiveState();
}

export function applyButtonStyle(style) {
    const safe = BUTTON_STYLES.includes(style) ? style : DEFAULT_BUTTON_STYLE;
    if (document.body) {
        document.body.dataset.buttonStyle = safe;
    }
    setSetting('buttonStyle', safe);
    refreshStyleSwatchActiveState();
}

function refreshThemeSwatchActiveState() {
    const current = getSetting('buttonTheme') || DEFAULT_BUTTON_THEME;
    document.querySelectorAll('.theme-swatch').forEach(el => {
        el.classList.toggle('active', el.dataset.theme === current);
    });
}

function refreshStyleSwatchActiveState() {
    const current = getSetting('buttonStyle') || DEFAULT_BUTTON_STYLE;
    document.querySelectorAll('.style-swatch').forEach(el => {
        el.classList.toggle('active', el.dataset.style === current);
    });
}

let isSettingsWired = false;

/**
 * Open the settings modal. Wires up listeners on first call.
 */
export function showSettingsMenu() {
    const modal = document.getElementById('settingsModal');
    if (!modal) return;

    if (!isSettingsWired) {
        const closeBtn = document.getElementById('settingsCloseButton');
        if (closeBtn) closeBtn.addEventListener('click', hideSettingsMenu);

        const changeUsernameBtn = document.getElementById('changeUsernameButton');
        if (changeUsernameBtn) {
            changeUsernameBtn.addEventListener('click', () => {
                hideSettingsMenu();
                // Lazy-import to avoid a module cycle (modals.js imports
                // userActions.js which imports views.js which transitively
                // touches settings.js).
                import('./modals').then(({ showUsernameModal }) => {
                    showUsernameModal({ prefillCurrent: true });
                });
            });
        }

        document.querySelectorAll('#themeGrid .theme-swatch').forEach(swatch => {
            const handler = () => applyButtonTheme(swatch.dataset.theme);
            swatch.addEventListener('click', handler);
            swatch.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handler();
                }
            });
        });

        document.querySelectorAll('#styleGrid .style-swatch').forEach(swatch => {
            const handler = () => applyButtonStyle(swatch.dataset.style);
            swatch.addEventListener('click', handler);
            swatch.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handler();
                }
            });
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.style.display !== 'none') {
                hideSettingsMenu();
            }
        });

        wireResetAccountFlow();

        isSettingsWired = true;
    }

    modal.style.display = 'block';
    refreshThemeSwatchActiveState();
    refreshStyleSwatchActiveState();
    refreshAccountSection();
    refreshAdminSection();
}

// Reveal the Admin section if-and-only-if the current user is an owner.
// Server enforces the real check; this is just to keep the UI tidy for
// everyone else. Lazy-imported to avoid pulling admin.js into the main
// settings flow for non-owners.
function refreshAdminSection() {
    const section = document.getElementById('settingsAdminSection');
    if (!section) return;
    // Resolve the current user lazily — settings.js avoids importing
    // views.js (would create a cycle). Read the username from the DOM
    // and the email from the Kinde session.
    const display = document.getElementById('usernameDisplay');
    const username = (display && display.textContent.trim()) || '';
    import('./admin.js').then(({ refreshAdminEntryVisibility }) => {
        // Pull email if Kinde has it cached on window.__kindeUser__; the
        // refresh fn accepts a plain {username,email} object.
        const email = (typeof window !== 'undefined' && window.__currentUserEmail__) || '';
        refreshAdminEntryVisibility({ username, email });
    }).catch(err => console.warn('[settings] admin lazy-load failed:', err));
}

function refreshAccountSection() {
    const valueEl = document.getElementById('settingsUsernameValue');
    if (!valueEl) return;
    // Read directly from the visible username display so we don't need to
    // import getUser here (avoids another module cycle).
    const display = document.getElementById('usernameDisplay');
    valueEl.textContent = (display && display.textContent.trim()) || 'Not set';
}

export function hideSettingsMenu() {
    const modal = document.getElementById('settingsModal');
    if (modal) modal.style.display = 'none';
}

// Two-step "are you really really sure" confirmation for Reset Account.
// First confirm just opens the second; the second one is the one that
// actually fires the wipe. Cancel buttons at either step abort.
function wireResetAccountFlow() {
    const resetBtn = document.getElementById('resetAccountButton');
    const confirm1 = document.getElementById('resetConfirmModal1');
    const confirm2 = document.getElementById('resetConfirmModal2');
    if (!resetBtn || !confirm1 || !confirm2) return;

    const yes1 = document.getElementById('resetConfirm1Yes');
    const cancel1 = document.getElementById('resetConfirm1Cancel');
    const yes2 = document.getElementById('resetConfirm2Yes');
    const cancel2 = document.getElementById('resetConfirm2Cancel');

    const close1 = () => { confirm1.style.display = 'none'; };
    const close2 = () => { confirm2.style.display = 'none'; };

    resetBtn.addEventListener('click', () => {
        hideSettingsMenu();
        confirm1.style.display = 'flex';
    });
    cancel1?.addEventListener('click', close1);
    yes1?.addEventListener('click', () => {
        close1();
        confirm2.style.display = 'flex';
    });
    cancel2?.addEventListener('click', close2);
    yes2?.addEventListener('click', async () => {
        // Disable both confirm buttons during the wipe so a double-click
        // doesn't fire the reset twice.
        yes2.disabled = true;
        cancel2.disabled = true;
        try {
            const { resetAccount } = await import('./resetAccount.js');
            await resetAccount();
        } finally {
            yes2.disabled = false;
            cancel2.disabled = false;
            close2();
        }
    });

    // Click outside the inner content closes the matching overlay.
    confirm1.addEventListener('click', (e) => { if (e.target === confirm1) close1(); });
    confirm2.addEventListener('click', (e) => { if (e.target === confirm2) close2(); });
}

/**
 * Apply the persisted theme as soon as <body> is available.
 * Called from index.js after DOMContentLoaded.
 */
export function initButtonTheme() {
    applyButtonTheme(getSetting('buttonTheme'));
    applyButtonStyle(getSetting('buttonStyle'));
}

// Load settings when the module is imported
loadSettings();
